import React, { useState, useEffect, useRef, useCallback } from 'react';
import {useIntl} from "react-intl";
import messages from "../../i18n/messages";

const GERMAN_ALPHABET = [
    'A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M',
    'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z',
    'Ä', 'Ö', 'Ü', 'ß', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9',
];
const COLORS = ['#FF0000', '#0000FF', '#006400', '#000000', '#800080'];
const GAME_AREA_HEIGHT = 500;
const MAX_LETTERS = 5;

const TypingGame = () => {
    const intl = useIntl();
    const [letters, setLetters] = useState([]);
    const [bullets, setBullets] = useState([]);
    const [cannonRotation, setCannonRotation] = useState(0);
    const [speed, setSpeed] = useState(3);
    const [score, setScore] = useState(0);
    const [isGameStarted, setIsGameStarted] = useState(false);
    const [gameDuration, setGameDuration] = useState(60);
    const [timeLeft, setTimeLeft] = useState(0);
    const containerRef = useRef(null);
    const rotationTimeout = useRef(null);
    const gameTimer = useRef(null);
    const letterInterval = useRef(null);
    const virtualInputRef = useRef(null);

    const generateLetter = useCallback(() => ({
        id: Date.now() + Math.random(),
        char: GERMAN_ALPHABET[Math.floor(Math.random() * GERMAN_ALPHABET.length)],
        position: 0,
        speed: speed * 0.5 + Math.random(),
        color: COLORS[Math.floor(Math.random() * COLORS.length)],
        left: `${Math.random() * 85}%`
    }), [speed]);

    const calculateAngle = useCallback((targetX, targetY) => {
        if (!containerRef.current) return 0;
        const containerWidth = containerRef.current.offsetWidth;
        const cannonX = containerWidth * 0.5;
        const cannonY = GAME_AREA_HEIGHT - 30;

        const targetPixelX = (parseFloat(targetX) / 100) * containerWidth;
        const deltaX = targetPixelX - cannonX;
        const deltaY = targetY - cannonY;

        const angleRad = Math.atan2(deltaX, -deltaY);
        const angleDeg = angleRad * (180 / Math.PI);

        return Math.min(45, Math.max(-45, angleDeg));
    }, []);

    const shootBullet = useCallback((target) => {
        if (!containerRef.current) return;

        if (rotationTimeout.current) {
            clearTimeout(rotationTimeout.current);
        }

        const angle = calculateAngle(target.left, target.position);
        setCannonRotation(angle);

        const containerWidth = containerRef.current.offsetWidth;
        setBullets(prev => [...prev, {
            id: Date.now(),
            targetId: target.id,
            startX: containerWidth * 0.5,
            startY: GAME_AREA_HEIGHT - 30,
            targetX: (parseFloat(target.left) / 100) * containerWidth,
            targetY: target.position,
            progress: 0
        }]);

        rotationTimeout.current = setTimeout(() => {
            setCannonRotation(0);
        }, 500);
    }, [calculateAngle]);

    const startGame = () => {
        setIsGameStarted(true);
        setScore(0);
        setLetters([]);
        setBullets([]);
        setTimeLeft(gameDuration);

        setTimeout(() => {
            letterInterval.current = setInterval(() => {
                if (letters.length < MAX_LETTERS) {
                    setLetters(prev => [...prev, generateLetter()]);
                }
            }, 2500 - speed * 200);
        }, 2000);

        gameTimer.current = setInterval(() => {
            setTimeLeft(prev => {
                if (prev <= 1) {
                    endGame();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    const endGame = () => {
        clearInterval(letterInterval.current);
        clearInterval(gameTimer.current);
        if (rotationTimeout.current) clearTimeout(rotationTimeout.current);
        setIsGameStarted(false);
        setLetters([]);
        setBullets([]);
    };

    useEffect(() => {
        const handleKeyPress = (e) => {
            if (!isGameStarted) return;

            let char = e.key.toUpperCase();
            if (e.key === 'ß') char = 'ß';

            setLetters(prev => {
                const target = prev.find(l => l.char === char);
                if (target) shootBullet(target);
                return prev;
            });
        };

        window.addEventListener('keydown', handleKeyPress);
        return () => window.removeEventListener('keydown', handleKeyPress);
    }, [shootBullet, isGameStarted]);

    useEffect(() => {
        if (!isGameStarted) return;

        const interval = setInterval(() => {
            setLetters(prev =>
                prev.map(l => ({
                    ...l,
                    position: Math.min(l.position + l.speed, GAME_AREA_HEIGHT - 30)
                })).filter(l => l.position < GAME_AREA_HEIGHT - 30)
            );
        }, 50);

        return () => clearInterval(interval);
    }, [isGameStarted]);

    useEffect(() => {
        if (!isGameStarted) return;

        const interval = setInterval(() => {
            setBullets(prev => {
                const updated = prev.map(b => ({
                    ...b,
                    progress: Math.min(1, b.progress + 0.03)
                }));

                const hitLetters = new Set();
                updated.forEach(bullet => {
                    if (bullet.progress >= 1) {
                        hitLetters.add(bullet.targetId);
                        setScore(s => s + 10);
                    }
                });

                if (hitLetters.size > 0) {
                    setLetters(prev => prev.filter(l => !hitLetters.has(l.id)));
                }

                return updated.filter(b => b.progress < 1);
            });
        }, 16);

        return () => clearInterval(interval);
    }, [isGameStarted]);

    // 新增移动端输入处理
    const handleMobileInput = () => {
        if (virtualInputRef.current) {
            virtualInputRef.current.focus();
            virtualInputRef.current.click();
        }
    };

    // 修改游戏区域点击处理
    const handleGameAreaClick = () => {
        if (isGameStarted && window.innerWidth < 768) { // 仅在移动端触发
            handleMobileInput();
        }
    };

    return (
        <div style={styles.container}>
            {/* 添加虚拟输入框 */}
            <input
                ref={virtualInputRef}
                style={styles.virtualInput}
                type="text"
                onChange={() => {}}
                onKeyDown={(e) => {
                    // 复用桌面端键盘处理逻辑
                    let char = e.key.toUpperCase();
                    if (e.key === 'ß') char = 'ß';

                    setLetters(prev => {
                        const target = prev.find(l => l.char === char);
                        if (target) shootBullet(target);
                        return prev;
                    });
                }}
            />

            <div style={styles.controls}>
                {!isGameStarted ? (
                    <div style={styles.settingPanel}>
                        <div style={styles.settingGroup}>
                            <label>
                                {intl.formatMessage(messages.gameTime)}:
                                <select
                                    value={gameDuration / 60}
                                    onChange={(e) => setGameDuration(Number(e.target.value) * 60)}
                                >
                                    {[1, 2, 3, 4, 5].map(min => (
                                        <option key={min} value={min}>{min}</option>
                                    ))}
                                </select>
                            </label>
                        </div>
                        <div style={styles.settingGroup}>
                            <label>
                                {intl.formatMessage(messages.typingGameAdjustSpeed)}:
                                <input
                                    type="range"
                                    min="1"
                                    max="10"
                                    value={speed}
                                    onChange={(e) => setSpeed(Number(e.target.value))}
                                />
                            </label>
                        </div>
                        <button
                            onClick={startGame}
                            style={styles.controlButton}
                        >
                            {intl.formatMessage(messages.gameStart)}
                        </button>
                    </div>
                ) : (
                    <div style={styles.gameInfo}>
                        <div>{intl.formatMessage(messages.gameTimeLeft)}: {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}</div>
                        <div>{intl.formatMessage(messages.typingGameCurrentScore)}: {score}</div>
                        <button
                            onClick={endGame}
                            style={{...styles.controlButton, backgroundColor: '#ff4444'}}
                        >
                            {intl.formatMessage(messages.gameEndEarly)}
                        </button>
                    </div>
                )}
            </div>

            <div ref={containerRef} style={styles.gameArea} onClick={handleGameAreaClick}>
                {/* 炮塔 */}
                <div style={{
                    ...styles.cannon,
                    transform: `rotate(${cannonRotation}deg)`
                }}>
                    <div style={styles.cannonBarrel} />
                </div>

                {/* 子弹 */}
                {bullets.map(bullet => {
                    const x = bullet.startX + (bullet.targetX - bullet.startX) * bullet.progress;
                    const y = bullet.startY + (bullet.targetY - bullet.startY) * bullet.progress;

                    return (
                        <div
                            key={bullet.id}
                            style={{
                                ...styles.bullet,
                                left: x,
                                top: y,
                                opacity: 1 - bullet.progress * 0.5
                            }}
                        />
                    );
                })}

                {/* 字母 */}
                {letters.map(letter => (
                    <div
                        key={letter.id}
                        style={{
                            ...styles.letter,
                            color: letter.color,
                            top: letter.position,
                            left: letter.left,
                            transform: `translateY(-50%)`
                        }}
                    >
                        {letter.char}
                    </div>
                ))}
            </div>

            {/* 移动端提示 */}
            {isGameStarted && window.innerWidth < 768 && (
                <div style={styles.mobileTip}>
                    点击任意位置打开键盘
                </div>
            )}

            {!isGameStarted && timeLeft === 0 && score > 0 && (
                <div style={styles.gameOver}>
                    <h2>{intl.formatMessage(messages.gameEndScore)}: {score}</h2>
                    <button
                        onClick={startGame}
                        style={styles.controlButton}
                    >
                        {intl.formatMessage(messages.gamePlayAgain)}
                    </button>
                </div>
            )}
        </div>
    );
};

const styles = {
    virtualInput: {
        position: 'absolute',
        opacity: 0,
        height: 0,
        width: 0,
        pointerEvents: 'none'
    },
    mobileTip: {
        textAlign: 'center',
        color: '#666',
        marginTop: '10px',
        fontSize: '14px'
    },
    container: {
        maxWidth: 800,
        margin: '20px auto',
        fontFamily: 'Arial, sans-serif'
    },
    controls: {
        padding: '20px',
        backgroundColor: '#f0f0f0',
        borderRadius: '8px',
        marginBottom: '20px',
    },
    settingPanel: {
        display: 'flex',
        flexDirection: 'column',
        gap: '15px'
    },
    settingGroup: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px'
    },
    controlButton: {
        padding: '8px 16px',
        backgroundColor: '#4CAF50',
        color: 'white',
        border: 'none',
        borderRadius: '4px',
        cursor: 'pointer',
        fontSize: '16px',
        transition: 'background-color 0.3s',
        '&:hover': {
            backgroundColor: '#45a049'
        }
    },
    gameInfo: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: '20px'
    },
    gameOver: {
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'rgba(0,0,0,0.9)',
        color: 'white',
        padding: '30px',
        borderRadius: '10px',
        textAlign: 'center',
        boxShadow: '0 0 20px rgba(0,0,0,0.3)',
        zIndex: 100
    },
    gameArea: {
        position: 'relative',
        height: GAME_AREA_HEIGHT,
        backgroundColor: '#f8f9fa',
        border: '2px solid #333',
        borderRadius: '8px',
        overflow: 'hidden'
    },
    cannon: {
        position: 'absolute',
        bottom: 0,
        left: '50%',
        transformOrigin: 'bottom center',
        transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        zIndex: 2
    },
    cannonBarrel: {
        width: '15px',
        height: '30px',
        backgroundColor: '#495057',
        transform: 'translateX(-50%)',
        borderRadius: '5px',
        boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
    },
    bullet: {
        position: 'absolute',
        width: 8,
        height: 8,
        backgroundColor: '#ffd700',
        borderRadius: '50%',
        transform: 'translate(-50%, -50%)',
        transition: 'left 0.05s linear, top 0.05s linear'
    },
    letter: {
        position: 'absolute',
        fontSize: 28,
        fontWeight: 'bold',
        transition: 'top 0.3s ease-out',
        userSelect: 'none',
        textShadow: '0 2px 4px rgba(0,0,0,0.3)'
    }
};

export default TypingGame;